import { useEffect, useState } from 'react';

export type FontScale = 'sm' | 'md' | 'lg';

const STORAGE_KEY = 'font-scale-preference';
const DEFAULT_SCALE: FontScale = 'md';

function isFontScale(value: string | null): value is FontScale {
  return value === 'sm' || value === 'md' || value === 'lg';
}

/**
 * Accessibility text-size toggle (backlog 2026-07-13, WCAG 1.4.4 — Resize
 * Text). Small/Medium/Large, scoped to body/content text only — headings,
 * nav chrome, and buttons do not consume the underlying --content-font-scale
 * CSS variable (see tokens.css) and stay pixel-identical across all three
 * settings.
 *
 * Modeled on useTheme.ts’s localStorage pattern: read/apply on mount, write
 * on change. Applies via a `data-font-scale` attribute on <html> (mirrors
 * useTheme’s `.dark` class toggle) rather than changing root font-size,
 * which would also scale headings.
 *
 * MVP is localStorage-only (per-device) — cross-device sync via
 * User.uiPreferences is a deliberately deferred follow-on, not implemented
 * here.
 */
export function useFontScale() {
  const [fontScale, setFontScaleState] = useState<FontScale>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isFontScale(stored)) return stored;
    } catch {
      // localStorage unavailable (private mode / quota) — fall through to default
    }
    return DEFAULT_SCALE;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-font-scale', fontScale);
  }, [fontScale]);

  const setFontScale = (scale: FontScale) => {
    setFontScaleState(scale);
    try {
      localStorage.setItem(STORAGE_KEY, scale);
    } catch {
      // localStorage unavailable â the choice just won’t persist
    }
  };

  const resetToDefault = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable — nothing to clear
    }
    setFontScaleState(DEFAULT_SCALE);
  };

  return { fontScale, setFontScale, resetToDefault };
}
